'use client';
import React, { useState, useEffect, useRef } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// ─── Constants ────────────────────────────────────────────────────────────────
const CATEGORIES = [
  { id: 'finance', label: 'Islamic Finance', icon: 'BanknotesIcon' },
  { id: 'legal', label: 'Halal Legal', icon: 'ScaleIcon' },
  { id: 'business', label: 'Business Strategy', icon: 'BriefcaseIcon' },
  { id: 'tech', label: 'Tech & Digital', icon: 'ComputerDesktopIcon' },
  { id: 'marketing', label: 'Halal Marketing', icon: 'MegaphoneIcon' },
  { id: 'education', label: 'Islamic Education', icon: 'AcademicCapIcon' },
  { id: 'health', label: 'Halal Health', icon: 'HeartIcon' },
  { id: 'general', label: 'General Consulting', icon: 'UserCircleIcon' },
];

const EXPERTISE_OPTIONS: Record<string, string[]> = {
  finance: ['Sukuk Structuring', 'Murabaha', 'Zakat Calculation', 'AAOIFI Standards', 'Shariah Audit', 'Islamic Banking', 'Waqf Management', 'Takaful'],
  legal: ['Halal Certification', 'Islamic Contract Law', 'Shariah Compliance', 'Family Law', 'Inheritance (Mirath)', 'Waqf Legal', 'Business Contracts'],
  business: ['Market Entry', 'Business Plans', 'Pitch Decks', 'OIC Markets', 'Halal Supply Chain', 'Startup Advisory', 'SME Growth'],
  tech: ['Web Development', 'Mobile Apps', 'AI/ML', 'Cybersecurity', 'Cloud Architecture', 'Islamic Fintech', 'E-commerce'],
  marketing: ['Halal Branding', 'Social Media', 'Content Strategy', 'SEO/SEM', 'Influencer Marketing', 'Market Research'],
  education: ['Quran Teaching', 'Arabic Language', 'Islamic Studies', 'Curriculum Design', 'Online Learning', 'Youth Education'],
  health: ['Nutrition (Halal)', 'Mental Health', 'Wellness Coaching', 'Medical Ethics', 'Herbal Medicine'],
  general: ['Project Management', 'HR Consulting', 'Operations', 'Strategic Planning', 'Change Management'],
};

const LANGUAGES = ['Arabic', 'English', 'Urdu', 'Somali', 'Indonesian', 'Malay', 'Turkish', 'French', 'Swahili', 'Bengali', 'Hausa', 'Persian'];

const AVAILABILITY_OPTIONS = [
  { id: 'available', label: 'Available', color: 'text-emerald-600', bg: 'bg-emerald-50', dot: 'bg-emerald-500' },
  { id: 'limited', label: 'Limited', color: 'text-amber-600', bg: 'bg-amber-50', dot: 'bg-amber-500' },
  { id: 'busy', label: 'Busy', color: 'text-red-600', bg: 'bg-red-50', dot: 'bg-red-500' },
];

const RATE_TYPES = [
  { id: 'hourly', label: 'Per Hour' },
  { id: 'project', label: 'Per Project' },
  { id: 'retainer', label: 'Monthly Retainer' },
];

type Section = 'profile' | 'expertise' | 'credentials' | 'rates' | 'experience';

interface ProfileForm {
  displayName: string;
  professionalTitle: string;
  bio: string;
  location: string;
  languages: string[];
  profilePhotoUrl: string;
  category: string;
  expertiseAreas: string[];
  skills: string;
  yearsExperience: string;
  education: string;
  certifications: string;
  credentialsNotes: string;
  hourlyRateUsd: string;
  rateType: string;
  availabilityStatus: string;
  weeklyHoursAvailable: string;
  timezone: string;
  isVerified: boolean;
}

const EMPTY_FORM: ProfileForm = {
  displayName: '',
  professionalTitle: '',
  bio: '',
  location: '',
  languages: [],
  profilePhotoUrl: '',
  category: 'general',
  expertiseAreas: [],
  skills: '',
  yearsExperience: '',
  education: '',
  certifications: '',
  credentialsNotes: '',
  hourlyRateUsd: '',
  rateType: 'hourly',
  availabilityStatus: 'available',
  weeklyHoursAvailable: '20',
  timezone: 'UTC',
  isVerified: false,
};

// ─── Tag Pill ─────────────────────────────────────────────────────────────────
function TagPill({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-500">
      {label}
      <button type="button" onClick={onRemove} className="hover:text-destructive transition-colors ml-0.5">
        <Icon name="XMarkIcon" size={12} />
      </button>
    </span>
  );
}

// ─── Section Card ─────────────────────────────────────────────────────────────
function SectionCard({ id, activeSection, icon, title, subtitle, children, onToggle }: {
  id: Section;
  activeSection: Section | null;
  icon: string;
  title: string;
  subtitle: string;
  children: React.ReactNode;
  onToggle: (id: Section) => void;
}) {
  const isOpen = activeSection === id;
  return (
    <div className={`bg-card border rounded-2xl overflow-hidden transition-all duration-200 ${isOpen ? 'border-primary/30 shadow-md' : 'border-border'}`}>
      <button
        type="button"
        onClick={() => onToggle(id)}
        className="w-full flex items-center gap-4 px-6 py-5 hover:bg-muted/40 transition-colors"
      >
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isOpen ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
          <Icon name={icon as Parameters<typeof Icon>[0]['name']} size={20} />
        </div>
        <div className="flex-1 text-left min-w-0">
          <p className="text-sm font-600 text-foreground">{title}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
        </div>
        <Icon
          name={isOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'}
          size={18}
          className="text-muted-foreground flex-shrink-0"
        />
      </button>
      {isOpen && (
        <div className="px-6 pb-6 border-t border-border/60 pt-5">
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ConsultantProfileEditPage() {
  const supabase = createClient();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState<ProfileForm>(EMPTY_FORM);
  const [activeSection, setActiveSection] = useState<Section | null>('profile');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [langInput, setLangInput] = useState('');
  const [skillInput, setSkillInput] = useState('');

  // ─── Load existing profile ────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('consultant_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (data && !error) {
        setProfileId(data.id);
        setForm({
          displayName: data.display_name ?? '',
          professionalTitle: data.professional_title ?? '',
          bio: data.bio ?? '',
          location: data.location ?? '',
          languages: data.languages ?? [],
          profilePhotoUrl: data.profile_photo_url ?? '',
          category: data.category ?? 'general',
          expertiseAreas: data.expertise_areas ?? [],
          skills: (data.skills ?? []).join(', '),
          yearsExperience: data.years_experience?.toString() ?? '',
          education: data.education ?? '',
          certifications: (data.certifications ?? []).join(', '),
          credentialsNotes: data.credentials_notes ?? '',
          hourlyRateUsd: data.hourly_rate_usd?.toString() ?? '',
          rateType: data.rate_type ?? 'hourly',
          availabilityStatus: data.availability_status ?? 'available',
          weeklyHoursAvailable: data.weekly_hours_available?.toString() ?? '20',
          timezone: data.timezone ?? 'UTC',
          isVerified: data.is_verified ?? false,
        });
      }
      setLoading(false);
    })();
  }, [user]);

  const showToast = (type: 'success' | 'error', msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const set = (field: keyof ProfileForm, value: unknown) =>
    setForm(prev => ({ ...prev, [field]: value }));

  // ─── Photo upload ─────────────────────────────────────────────────────────
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setUploadingPhoto(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `consultant-photos/${user.id}-${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('public-assets')
        .upload(path, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase.storage.from('public-assets').getPublicUrl(path);
      set('profilePhotoUrl', urlData.publicUrl);
      showToast('success', 'Photo uploaded successfully');
    } catch {
      showToast('error', 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // ─── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const payload = {
        user_id: user.id,
        display_name: form.displayName,
        professional_title: form.professionalTitle,
        bio: form.bio,
        location: form.location,
        languages: form.languages,
        profile_photo_url: form.profilePhotoUrl,
        category: form.category,
        expertise_areas: form.expertiseAreas,
        skills: form.skills.split(',').map(s => s.trim()).filter(Boolean),
        years_experience: parseInt(form.yearsExperience) || 0,
        education: form.education,
        certifications: form.certifications.split(',').map(s => s.trim()).filter(Boolean),
        credentials_notes: form.credentialsNotes,
        hourly_rate_usd: parseFloat(form.hourlyRateUsd) || 0,
        rate_type: form.rateType,
        availability_status: form.availabilityStatus,
        weekly_hours_available: parseInt(form.weeklyHoursAvailable) || 20,
        timezone: form.timezone,
        is_verified: form.isVerified,
        updated_at: new Date().toISOString(),
      };

      if (profileId) {
        const { error } = await supabase
          .from('consultant_profiles')
          .update(payload)
          .eq('id', profileId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('consultant_profiles')
          .insert({ ...payload, onboarding_completed: true })
          .select('id')
          .single();
        if (error) throw error;
        setProfileId(data.id);
      }
      showToast('success', 'Profile saved successfully');
    } catch {
      showToast('error', 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (id: Section) =>
    setActiveSection(prev => (prev === id ? null : id));

  const toggleExpertise = (tag: string) => {
    set('expertiseAreas', form.expertiseAreas.includes(tag)
      ? form.expertiseAreas.filter(t => t !== tag)
      : [...form.expertiseAreas, tag]);
  };

  const addLanguage = (lang: string) => {
    if (lang && !form.languages.includes(lang)) {
      set('languages', [...form.languages, lang]);
    }
    setLangInput('');
  };

  const addSkillTag = () => {
    if (skillInput.trim()) {
      const existing = form.skills ? form.skills.split(',').map(s => s.trim()).filter(Boolean) : [];
      if (!existing.includes(skillInput.trim())) {
        set('skills', [...existing, skillInput.trim()].join(', '));
      }
      setSkillInput('');
    }
  };

  const availConfig = AVAILABILITY_OPTIONS.find(a => a.id === form.availabilityStatus) ?? AVAILABILITY_OPTIONS[0];

  if (loading) {
    return (
      <AppLayout activeRoute="/consultant-profile-edit">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-muted-foreground">Loading your profile…</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout activeRoute="/consultant-profile-edit">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-sm font-500 transition-all duration-300 ${
          toast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-destructive text-destructive-foreground'
        }`}>
          <Icon name={toast.type === 'success' ? 'CheckCircleIcon' : 'ExclamationCircleIcon'} size={18} />
          {toast.msg}
        </div>
      )}

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-700 text-foreground">Edit Consultant Profile</h1>
            <p className="text-sm text-muted-foreground mt-1">Keep your profile up to date to attract the right clients</p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-600 hover:bg-primary/90 transition-colors disabled:opacity-60 flex-shrink-0"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
            ) : (
              <Icon name="CheckIcon" size={16} />
            )}
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>

        {/* Profile Photo Preview */}
        <div className="bg-card border border-border rounded-2xl p-6 mb-4 flex items-center gap-5">
          <div className="relative flex-shrink-0">
            {form.profilePhotoUrl ? (
              <img
                src={form.profilePhotoUrl}
                alt={`${form.displayName} profile photo`}
                className="w-20 h-20 rounded-2xl object-cover border-2 border-border"
              />
            ) : (
              <div className="w-20 h-20 rounded-2xl gradient-teal flex items-center justify-center text-white text-2xl font-700">
                {form.displayName ? form.displayName.charAt(0).toUpperCase() : '?'}
              </div>
            )}
            {uploadingPhoto && (
              <div className="absolute inset-0 rounded-2xl bg-black/50 flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-600 text-foreground mb-0.5">Profile Photo</p>
            <p className="text-xs text-muted-foreground mb-3">JPG or PNG, max 5MB. Square images work best.</p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/80 text-foreground text-xs font-500 rounded-lg transition-colors disabled:opacity-60"
              >
                <Icon name="ArrowUpTrayIcon" size={14} />
                {uploadingPhoto ? 'Uploading…' : 'Upload Photo'}
              </button>
              {form.profilePhotoUrl && (
                <button
                  type="button"
                  onClick={() => set('profilePhotoUrl', '')}
                  className="text-xs text-muted-foreground hover:text-destructive transition-colors"
                >
                  Remove
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handlePhotoUpload}
            />
          </div>
          {/* Verification badge */}
          <div className={`flex-shrink-0 flex flex-col items-center gap-1.5 px-4 py-3 rounded-xl border ${form.isVerified ? 'bg-emerald-50 border-emerald-200' : 'bg-muted border-border'}`}>
            <Icon
              name={form.isVerified ? 'ShieldCheckIcon' : 'ShieldExclamationIcon'}
              size={24}
              className={form.isVerified ? 'text-emerald-600' : 'text-muted-foreground'}
            />
            <span className={`text-xs font-600 ${form.isVerified ? 'text-emerald-700' : 'text-muted-foreground'}`}>
              {form.isVerified ? 'Verified' : 'Unverified'}
            </span>
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-3">

          {/* ── Profile Section ── */}
          <SectionCard
            id="profile"
            activeSection={activeSection}
            icon="UserCircleIcon"
            title="Basic Profile"
            subtitle="Name, title, bio, location, and languages"
            onToggle={toggleSection}
          >
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-600 text-foreground mb-1.5">Display Name *</label>
                  <input
                    type="text"
                    value={form.displayName}
                    onChange={e => set('displayName', e.target.value)}
                    placeholder="Your full name"
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-600 text-foreground mb-1.5">Professional Title *</label>
                  <input
                    type="text"
                    value={form.professionalTitle}
                    onChange={e => set('professionalTitle', e.target.value)}
                    placeholder="e.g. Islamic Finance Consultant"
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-600 text-foreground mb-1.5">Bio</label>
                <textarea
                  value={form.bio}
                  onChange={e => set('bio', e.target.value)}
                  rows={4}
                  placeholder="Describe your background, expertise, and what clients can expect from working with you…"
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors resize-none"
                />
                <p className="text-xs text-muted-foreground mt-1">{form.bio.length} / 1000 characters</p>
              </div>

              <div>
                <label className="block text-xs font-600 text-foreground mb-1.5">Location</label>
                <input
                  type="text"
                  value={form.location}
                  onChange={e => set('location', e.target.value)}
                  placeholder="City, Country"
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-600 text-foreground mb-1.5">Languages</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {form.languages.map(lang => (
                    <TagPill key={lang} label={lang} onRemove={() => set('languages', form.languages.filter(l => l !== lang))} />
                  ))}
                </div>
                <div className="flex gap-2">
                  <select
                    value={langInput}
                    onChange={e => setLangInput(e.target.value)}
                    className="flex-1 px-3 py-2 bg-background border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                  >
                    <option value="">Select a language…</option>
                    {LANGUAGES.filter(l => !form.languages.includes(l)).map(l => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => addLanguage(langInput)}
                    disabled={!langInput}
                    className="px-4 py-2 bg-primary/10 text-primary rounded-xl text-sm font-500 hover:bg-primary/20 transition-colors disabled:opacity-40"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          </SectionCard>

          {/* ── Expertise Section ── */}
          <SectionCard
            id="expertise"
            activeSection={activeSection}
            icon="AcademicCapIcon"
            title="Expertise & Skills"
            subtitle="Category, expertise areas, and specific skills"
            onToggle={toggleSection}
          >
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-600 text-foreground mb-2">Primary Category</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {CATEGORIES.map(cat => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => { set('category', cat.id); set('expertiseAreas', []); }}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-xs font-500 transition-all duration-150 ${
                        form.category === cat.id
                          ? 'border-primary bg-primary/10 text-primary' :'border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground'
                      }`}
                    >
                      <Icon name={cat.icon as Parameters<typeof Icon>[0]['name']} size={18} />
                      <span className="text-center leading-tight">{cat.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {form.category && EXPERTISE_OPTIONS[form.category] && (
                <div>
                  <label className="block text-xs font-600 text-foreground mb-2">Expertise Areas</label>
                  <div className="flex flex-wrap gap-2">
                    {EXPERTISE_OPTIONS[form.category].map(tag => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => toggleExpertise(tag)}
                        className={`px-3 py-1.5 rounded-full text-xs font-500 border transition-all duration-150 ${
                          form.expertiseAreas.includes(tag)
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background text-muted-foreground border-border hover:border-primary/40 hover:text-foreground'
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-600 text-foreground mb-1.5">Additional Skills</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {form.skills.split(',').map(s => s.trim()).filter(Boolean).map(skill => (
                    <TagPill
                      key={skill}
                      label={skill}
                      onRemove={() => set('skills', form.skills.split(',').map(s => s.trim()).filter(s => s !== skill).join(', '))}
                    />
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={skillInput}
                    onChange={e => setSkillInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSkillTag(); } }}
                    placeholder="Type a skill and press Add"
                    className="flex-1 px-3 py-2 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                  />
                  <button
                    type="button"
                    onClick={addSkillTag}
                    disabled={!skillInput.trim()}
                    className="px-4 py-2 bg-primary/10 text-primary rounded-xl text-sm font-500 hover:bg-primary/20 transition-colors disabled:opacity-40"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          </SectionCard>

          {/* ── Credentials Section ── */}
          <SectionCard
            id="credentials"
            activeSection={activeSection}
            icon="IdentificationIcon"
            title="Credentials & Verification"
            subtitle="Education, certifications, and verification status"
            onToggle={toggleSection}
          >
            <div className="space-y-4">
              {/* Verification toggle */}
              <div className={`flex items-center justify-between p-4 rounded-xl border ${form.isVerified ? 'bg-emerald-50 border-emerald-200' : 'bg-muted border-border'}`}>
                <div className="flex items-center gap-3">
                  <Icon
                    name={form.isVerified ? 'ShieldCheckIcon' : 'ShieldExclamationIcon'}
                    size={22}
                    className={form.isVerified ? 'text-emerald-600' : 'text-muted-foreground'}
                  />
                  <div>
                    <p className="text-sm font-600 text-foreground">Verification Status</p>
                    <p className="text-xs text-muted-foreground">Verified consultants get a badge on their profile</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => set('isVerified', !form.isVerified)}
                  className={`relative w-12 h-6 rounded-full transition-colors duration-200 focus:outline-none ${form.isVerified ? 'bg-emerald-500' : 'bg-muted-foreground/30'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${form.isVerified ? 'translate-x-6' : 'translate-x-0'}`} />
                </button>
              </div>

              <div>
                <label className="block text-xs font-600 text-foreground mb-1.5">Education</label>
                <textarea
                  value={form.education}
                  onChange={e => set('education', e.target.value)}
                  rows={3}
                  placeholder="e.g. MBA in Islamic Finance, INCEIF University, 2018"
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors resize-none"
                />
              </div>

              <div>
                <label className="block text-xs font-600 text-foreground mb-1.5">Certifications</label>
                <input
                  type="text"
                  value={form.certifications}
                  onChange={e => set('certifications', e.target.value)}
                  placeholder="e.g. CIPA, CIFP, AAOIFI — separate with commas"
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                />
                <p className="text-xs text-muted-foreground mt-1">Separate multiple certifications with commas</p>
              </div>

              <div>
                <label className="block text-xs font-600 text-foreground mb-1.5">Additional Credentials Notes</label>
                <textarea
                  value={form.credentialsNotes}
                  onChange={e => set('credentialsNotes', e.target.value)}
                  rows={3}
                  placeholder="Any additional credentials, awards, or notable achievements…"
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors resize-none"
                />
              </div>
            </div>
          </SectionCard>

          {/* ── Rates Section ── */}
          <SectionCard
            id="rates"
            activeSection={activeSection}
            icon="BanknotesIcon"
            title="Hourly Rate & Pricing"
            subtitle="Set your rate and pricing model"
            onToggle={toggleSection}
          >
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-600 text-foreground mb-1.5">Hourly Rate (USD)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-500">$</span>
                    <input
                      type="number"
                      min="0"
                      step="5"
                      value={form.hourlyRateUsd}
                      onChange={e => set('hourlyRateUsd', e.target.value)}
                      placeholder="150"
                      className="w-full pl-7 pr-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-600 text-foreground mb-1.5">Rate Type</label>
                  <select
                    value={form.rateType}
                    onChange={e => set('rateType', e.target.value)}
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                  >
                    {RATE_TYPES.map(r => (
                      <option key={r.id} value={r.id}>{r.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-600 text-foreground mb-2">Availability Status</label>
                <div className="flex gap-2 flex-wrap">
                  {AVAILABILITY_OPTIONS.map(opt => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => set('availabilityStatus', opt.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-500 transition-all duration-150 ${
                        form.availabilityStatus === opt.id
                          ? `${opt.bg} border-current ${opt.color}`
                          : 'bg-background border-border text-muted-foreground hover:border-primary/40'
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full ${form.availabilityStatus === opt.id ? opt.dot : 'bg-muted-foreground/40'}`} />
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-600 text-foreground mb-1.5">Weekly Hours Available</label>
                  <input
                    type="number"
                    min="1"
                    max="80"
                    value={form.weeklyHoursAvailable}
                    onChange={e => set('weeklyHoursAvailable', e.target.value)}
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-600 text-foreground mb-1.5">Timezone</label>
                  <select
                    value={form.timezone}
                    onChange={e => set('timezone', e.target.value)}
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                  >
                    {['UTC', 'Africa/Nairobi', 'Africa/Lagos', 'Africa/Cairo', 'Asia/Riyadh', 'Asia/Dubai', 'Asia/Karachi', 'Asia/Dhaka', 'Asia/Kuala_Lumpur', 'Asia/Jakarta', 'Asia/Istanbul', 'Europe/London', 'America/New_York'].map(tz => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </SectionCard>

          {/* ── Experience Section ── */}
          <SectionCard
            id="experience"
            activeSection={activeSection}
            icon="BriefcaseIcon"
            title="Experience Details"
            subtitle="Years of experience and professional background"
            onToggle={toggleSection}
          >
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-600 text-foreground mb-1.5">Years of Experience</label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0"
                    max="40"
                    value={form.yearsExperience || '0'}
                    onChange={e => set('yearsExperience', e.target.value)}
                    className="flex-1 accent-primary"
                  />
                  <div className="w-16 px-3 py-2 bg-primary/10 text-primary text-sm font-700 rounded-xl text-center flex-shrink-0">
                    {form.yearsExperience || '0'} yr{parseInt(form.yearsExperience) !== 1 ? 's' : ''}
                  </div>
                </div>
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>0</span>
                  <span>10</span>
                  <span>20</span>
                  <span>30</span>
                  <span>40+</span>
                </div>
              </div>

              {/* Experience milestones */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Entry Level', range: '0–2 yrs', min: 0, max: 2 },
                  { label: 'Mid Level', range: '3–7 yrs', min: 3, max: 7 },
                  { label: 'Senior', range: '8–15 yrs', min: 8, max: 15 },
                  { label: 'Expert', range: '15+ yrs', min: 15, max: 40 },
                ].map(tier => {
                  const yrs = parseInt(form.yearsExperience) || 0;
                  const active = yrs >= tier.min && yrs <= tier.max;
                  return (
                    <div
                      key={tier.label}
                      className={`p-3 rounded-xl border text-center transition-all ${active ? 'bg-primary/10 border-primary/30' : 'bg-muted/40 border-border'}`}
                    >
                      <p className={`text-xs font-600 ${active ? 'text-primary' : 'text-muted-foreground'}`}>{tier.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{tier.range}</p>
                    </div>
                  );
                })}
              </div>

              <div>
                <label className="block text-xs font-600 text-foreground mb-1.5">Professional Background</label>
                <textarea
                  value={form.education}
                  onChange={e => set('education', e.target.value)}
                  rows={4}
                  placeholder="Describe your professional journey, key roles, and notable projects…"
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors resize-none"
                />
              </div>
            </div>
          </SectionCard>
        </div>

        {/* Bottom Save */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl text-sm font-600 hover:bg-primary/90 transition-colors disabled:opacity-60"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
            ) : (
              <Icon name="CheckIcon" size={16} />
            )}
            {saving ? 'Saving…' : 'Save All Changes'}
          </button>
        </div>
      </div>
    </AppLayout>
  );
}
